import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { WaterPriceList, WaterPriceListCreateOrUpdate } from '@models/water-price-list';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmWaterPriceListsService } from '@services/crm-water-price-lists.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { toDateOnly } from '@utils/date-only';
import { finalize, firstValueFrom } from 'rxjs';
import {
  activePriceListId as findActivePriceListId,
  isFuturePriceList,
  withPriceListInOrder,
} from '../../../shared/pricing-grid/pricing-grid.model';
import { AddPriceListButtonComponent } from '../add-price-list-button/add-price-list-button.component';
import { priceListErrorKey } from '../price-list-error';
import { WaterPriceListDialogComponent } from '../water-price-list-dialog/water-price-list-dialog.component';
import type { WaterPriceListDialogData } from '../water-price-list-dialog/water-price-list-dialog.model';

/**
 * The lake's price lists (CS-232) — the dated containers all of its pricing is versioned by. Adding one
 * with a future start date is how next season's increase is prepared: the backend prices each stay from
 * the latest price list starting on or before the arrival date, so a future list is inert until that
 * date and the prices current bookings are made at are untouched.
 *
 * Unlike every other section of the Pricing tab, this one is **not** part of the page's staged submit:
 * it implements no `dirty()`/`validate()`/`save()` contract and the toolbar Save neither sends nor
 * validates it. Each row is written the moment it is confirmed, because a delete here is not an edit —
 * it cascades away every price stored for that year — and that has to be a deliberate act rather than a
 * row silently dropped from a whole-set replace.
 *
 * {@link changed} lets the tab bring the sibling pricing sections' dropdowns in step. The Pricing tab
 * is disabled until the lake exists, so {@link waterId} is always a real id.
 */
@Component({
  selector: 'app-water-price-lists',
  templateUrl: './water-price-lists.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    AddPriceListButtonComponent,
    TranslatePipe,
  ],
})
export class WaterPriceListsComponent implements OnInit {
  readonly waterId = input.required<number>();

  /**
   * The page's save is in flight. Writes here are withheld until it lands: deleting a price list that a
   * pending grid PUT is addressed to would make that PUT fail for a reason the user could not connect to
   * what they did.
   */
  readonly disabled = input(false);

  /** A price list was created, renamed, re-dated or deleted, so the tab's pricing grids are out of date. */
  readonly changed = output<void>();

  private readonly priceListsService = inject(CrmWaterPriceListsService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  /** The price lists, in the order the API returned them — newest first. */
  readonly priceLists = signal<WaterPriceList[]>([]);

  readonly isLoading = signal(false);

  /**
   * The section has nothing to show because a load failed, as opposed to because there is nothing to
   * show. Held apart from an empty {@link priceLists} so the two can say different things: a lake really
   * can have no price lists — that is the state this section exists to fix — while a failed GET means
   * nothing at all is known.
   */
  readonly loadFailed = signal(false);

  readonly displayedColumns = ['name', 'startDate', 'actions'];

  /** Whether the row actions and the add button are available. */
  readonly isBusy = computed(() => this.isLoading() || this.disabled());

  /**
   * Today as `yyyy-MM-dd`, for marking which price lists are staged ahead. Fixed at construction; a
   * session does not outlive a day.
   */
  private readonly today = toDateOnly(new Date()) ?? '';

  /**
   * The price list in effect today — the latest one that has started, which is what the backend prices
   * from right now. Marked so a user can tell at a glance which rows are history, which is live and which
   * are staged for the future. Shared with the pricing grids, which open on this same price list.
   */
  readonly activePriceListId = computed(() => findActivePriceListId(this.priceLists()));

  /**
   * Discards responses from superseded loads. Two writes confirmed in quick succession each trigger a
   * load, and without this the slower response could land last and show a stale set.
   */
  private loadToken = 0;

  constructor() {
    // Take in price lists created anywhere on the page, not just from this section's own button: each
    // pricing grid carries one beside its dropdown, and for a lake with none yet that is the button the
    // user actually reaches. The row is spliced in rather than re-read — the POST response is the stored
    // price list, so there is nothing a GET would add.
    this.priceListsService.created$.pipe(takeUntilDestroyed()).subscribe(({ waterId, priceList }) => {
      if (waterId !== this.waterId()) return;
      // Except when nothing is known: a single row on top of a failed load would read as the whole set.
      if (this.loadFailed()) {
        this.load();
        return;
      }
      this.priceLists.update((priceLists) => withPriceListInOrder(priceLists, priceList));
    });
  }

  ngOnInit(): void {
    this.load();
  }

  /** Bound to the retry button the failed state offers; nothing else re-attempts a load on its own. */
  protected retryLoad(): void {
    this.load();
  }

  private load(): void {
    const token = ++this.loadToken;
    this.isLoading.set(true);
    this.priceListsService
      .getPriceLists(this.waterId())
      .pipe(
        // `null`, not an empty set: a failure is the absence of an answer, and adopting `[]` for it would
        // have the section claim the lake has no price lists — the one thing it must not get wrong.
        showGenericErrorToast(this.toastService, () => null, this.translate.instant('waters.price-lists.load-failed')),
        finalize(() => {
          if (token === this.loadToken) this.isLoading.set(false);
        }),
      )
      .subscribe((priceLists) => {
        if (token !== this.loadToken) return;
        if (priceLists === null) {
          this.loadFailed.set(this.priceLists().length === 0);
          return;
        }
        this.loadFailed.set(false);
        this.priceLists.set(priceLists);
      });
  }

  /** Whether a price list has not taken effect yet, i.e. it is staged for the future. */
  isFuture(priceList: WaterPriceList): boolean {
    return isFuturePriceList(priceList, this.today);
  }

  protected onCreated(): void {
    this.changed.emit();
  }

  protected async edit(priceList: WaterPriceList): Promise<void> {
    const data: WaterPriceListDialogData = { priceList };
    const dto = await firstValueFrom(
      this.dialog
        .open<WaterPriceListDialogComponent, WaterPriceListDialogData, WaterPriceListCreateOrUpdate>(
          WaterPriceListDialogComponent,
          { data, width: '480px' },
        )
        .afterClosed(),
    );
    if (dto == null) return;

    try {
      await firstValueFrom(this.priceListsService.updatePriceList(this.waterId(), priceList.id, dto));
      this.toastService.showSuccess(this.translate.instant('waters.price-lists.save-success'));
      this.load();
      // A rename changes the label the grids' dropdowns show, and a re-date changes which list is live.
      this.changed.emit();
    } catch (error: unknown) {
      this.toastService.showError(this.translate.instant(priceListErrorKey(error, 'waters.price-lists.save-failed')));
    }
  }

  protected async delete(priceList: WaterPriceList): Promise<void> {
    // Spell out what goes with it, then make them type its name. The prices of this price list are removed
    // by database cascade, with no undo and nothing left to say what they were — far too much to lose to a
    // prompt someone clicked through without reading. Typing the name cannot be done without reading which
    // price list this is.
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.price-lists.delete-title'),
      prompt: this.translate.instant('waters.price-lists.delete-prompt', { name: priceList.name }),
      requirePhrase: priceList.name,
    });
    if (!confirmed) return;

    try {
      await firstValueFrom(this.priceListsService.deletePriceList(this.waterId(), priceList.id));
      this.toastService.showSuccess(this.translate.instant('waters.price-lists.delete-success'));
      this.load();
      this.changed.emit();
    } catch (error: unknown) {
      this.toastService.showError(this.translate.instant(priceListErrorKey(error, 'waters.price-lists.delete-failed')));
    }
  }
}
